import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFormik } from "formik"
import moment from "moment"
import { useSelector } from "react-redux"
import _ from "lodash"
import * as yup from "yup"
import { FormControl, InputAdornment, TextField, Button, Box, Typography } from "@material-ui/core"
import { useDispatch } from "react-redux"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import Simple from "./Simple"
import { checkNameAlreadyExist, checkNameAlreadyExistFailed, postSavedDateRange } from "../../../actions/createTimelineActions"
import { DatePicker } from "../../common/DatePicker"

const useStyles = makeStyles((theme) => ({
	root: {
		"& fieldset > legend": {
			float: "none",
		},
	},
	margin: {
		margin: theme.spacing(2),
	},

	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		width: "100%",
		gap: "4px",
	},
	formActionsBtn: {
		"&:disabled": {
			backgroundColor: theme.palette.grey[300],
		},
		textTransform: "none",
	},
	textField: {
		width: "327px",
		"& .MuiOutlinedInput-input": {
			paddingLeft: "12px",
			...theme.typography.body2,
		},
		"& input[type='text']:focus": {
			boxShadow: "none",
		},
		"& input[type='text'].Mui-disabled": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.background.default,
		},
	},
	fullWidth: {
		width: "450px",
	},
	inputFormControl: {
		padding: theme.spacing(1),
	},
	bulkDownloadDialog: {
		"& .MuiDialogTitle-root > h2": {
			...theme.typography.h6,
			fontWeight: theme.typography.fontWeightMedium,
			wordWrap: "break-word",
		},
	},
	formActionsCancelBtn: {
		color: theme.palette.grey["D100"],
		fontSize: theme.typography.fontSize,
		fontWeight: theme.typography.fontWeightMedium,
		textTransform: "none",
		"&:hover": {
			background: "none",
		},
	},
	flexContainer: {
		display: "flex",
		alignItems: "center",
		flexDirection: "row",
		gap: theme.spacing(3),
	},
	datePicker: {
		maxWidth: 210,
	},
	error: {
		padding: `0 ${theme.spacing(1)}px ${theme.spacing(1)}px`,
	},
}))

function SaveDateRangeDialog(props) {
	const theme = useTheme()
	const { selectedGraphDateRange, onClose, open } = props
	const classes = useStyles(theme)
	const [t] = useTranslation("global")
	const { isNameExist, graph } = useSelector((state) => state?.timeline)
	const dateRange = useSelector((state) => state.filter?.dateRange)
	const nameRef = useRef(null)

	const dispatch = useDispatch()

	const saveValidationSchema = yup.object({
		name: yup
			.string()
			.trim()
			.max(30, t("timeline.saveDialog.errorMsg.fileNameLimit"))
			.required(t("timeline.saveDialog.errorMsg.required")),
		start_date: yup
			.date()
			.required(t("timeline.saveDialog.errorMsg.required"))
			.when("end_date", (endDate, schema) => {
				return schema.test({
					name: "start-date",
					exclusive: true,
					message: t("timeline.saveDialog.errorMsg.date"),
					test: function (startDate) {
						const endDateValue = this.resolve(endDate)
						return !startDate || !endDateValue || startDate <= endDateValue
					},
				})
			}),
		end_date: yup
			.date()
			.required(t("timeline.saveDialog.errorMsg.required"))
			.test("end-date", t("timeline.saveDialog.errorMsg.date"), function (endDate) {
				const { start_date } = this.parent
				return !start_date || !endDate || endDate >= start_date
			}),
	})

	function handleSubmit(data) {
		const updatedData = { ...data }
		updatedData.start_date = moment(updatedData?.start_date).format("YYYY-MM-DD")
		updatedData.end_date = moment(updatedData?.end_date).format("YYYY-MM-DD")
		dispatch(postSavedDateRange(updatedData))
		props?.onClose()
		saveDateRangeForm?.resetForm()
	}

	const saveDateRangeForm = useFormik({
		initialValues: {
			name: "",
			start_date: new Date(),
			end_date: new Date(),
		},
		validationSchema: saveValidationSchema,
		onSubmit: handleSubmit,
	})

	const filteredProps = {
		...props,
		dynamicClass: classes.bulkDownloadDialog,
	}

	useEffect(() => {
		if (!selectedGraphDateRange?.sliderPicker?.start || !selectedGraphDateRange?.sliderPicker?.end) {
			if (graph?.length > 0) {
				saveDateRangeForm.setFieldValue("start_date", new Date(graph[0][0]).toISOString())
				saveDateRangeForm.setFieldValue("end_date", new Date(graph[graph.length - 1][0]).toISOString())
			}
		} else {
			saveDateRangeForm.setFieldValue("start_date", new Date(selectedGraphDateRange?.sliderPicker?.start).toISOString())
			saveDateRangeForm.setFieldValue("end_date", new Date(selectedGraphDateRange?.sliderPicker?.end).toISOString())
		}
	}, [selectedGraphDateRange, graph, open])

	useEffect(() => {
		if (dateRange?.startDate !== "") {
			saveDateRangeForm.setFieldValue("start_date", new Date(dateRange?.startDate).toISOString())
			saveDateRangeForm.setFieldValue("end_date", new Date(dateRange?.endDate).toISOString())
		}
	}, [open])

	useEffect(() => {
		if (nameRef.current) {
			nameRef.current.focus()
		}
	}, [])

	return (
		<Simple {...filteredProps}>
			<form className={classes.root} noValidate autoComplete="off" id="dateRangeName" onSubmit={saveDateRangeForm?.handleSubmit}>
				<FormControl className={`${classes.inputFormControl} ${classes.flexContainer}`}>
					<DatePicker
						label={`${t("timeline.saveDialog.startDate")}*`}
						date={saveDateRangeForm.values.start_date}
						setDate={(e) => {
							const date = e?._d
							if (!date) return
							saveDateRangeForm.setFieldValue("start_date", date)
						}}
						className={`${classes.datePicker}`}
					/>

					<DatePicker
						label={`${t("timeline.saveDialog.endDate")}*`}
						date={saveDateRangeForm.values.end_date}
						setDate={(e) => {
							const date = e?._d
							if (!date) return
							saveDateRangeForm.setFieldValue("end_date", date)
						}}
						className={`${classes.datePicker}`}
					/>
				</FormControl>
				<FormControl className={`${classes.inputFormControl} ${classes.flexContainer} ${classes.error}`}>
					{(saveDateRangeForm.errors.start_date || saveDateRangeForm.errors.end_date) && (
						<Typography variant="body2" color="error">
							{t("timeline.saveDialog.errorMsg.date")}
						</Typography>
					)}
				</FormControl>

				<FormControl className={classes.inputFormControl}>
					<TextField
						id="name"
						label={t("timeline.saveDialog.textFieldLabel")}
						variant="outlined"
						className={[classes.textField, classes.fullWidth]}
						value={saveDateRangeForm.values.name}
						onChange={saveDateRangeForm.handleChange}
						onBlur={() => {
							dispatch(checkNameAlreadyExist(saveDateRangeForm.values.name))
						}}
						error={saveDateRangeForm.touched.name && Boolean(saveDateRangeForm.errors.name)}
						helperText={saveDateRangeForm.touched.name && saveDateRangeForm.errors.name}
						inputRef={nameRef}
					/>
					{isNameExist && (
						<Typography style={{ ...theme.typography.errorHelperLable, color: theme.palette.error.main, paddingTop: "10px" }}>
							{t("timeline.saveDialog.nameExist")}
						</Typography>
					)}
				</FormControl>
				<FormControl className={classes.formActions}>
					<Button
						variant="contained"
						color="primary"
						className={classes.formActionsBtn}
						style={{
							padding: "4px 12px",
							background: theme.palette.primary.dark,
							fontWeight: theme.typography.fontWeightMedium,
						}}
						type="submit"
						disabled={!saveDateRangeForm.isValid || isNameExist}
					>
						{t("timeline.saveDialog.saveBtn")}
					</Button>
					<Button
						variant="text"
						className={classes.formActionsCancelBtn}
						onClick={() => {
							saveDateRangeForm?.resetForm()
							props?.onClose()
							dispatch(checkNameAlreadyExistFailed())
						}}
					>
						{t("timeline.saveDialog.cancelBtn")}
					</Button>
				</FormControl>
			</form>
		</Simple>
	)
}

export default SaveDateRangeDialog
